/**
 * Iter 74 · Manager Cashbook Widget.
 *
 * Prominent card on the Manager Home showing today's IN/OUT and live
 * cash-in-hand. One-tap "Quick deposit" opens the Bank Deposit dialog
 * so the manager can shovel cash to the bank without leaving Home.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Landmark, HandCoins, TrendingUp, TrendingDown } from "lucide-react";

import { useTranslation } from "react-i18next";
const rupee = (v) =>
  v == null ? "-" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function ManagerCashbookWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeposit, setShowDeposit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Both endpoints exist as of Iter 71/72.
      const [{ data: sum }, { data: today }] = await Promise.all([
        api.get("/manager/bank-deposits/summary"),
        api.get(`/cashbook?date=${new Date().toISOString().slice(0, 10)}`).catch(() => ({ data: null })),
      ]);
      setData({ sum, today });
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cih = Number(data?.sum?.cash_in_hand || 0);
  const cashIn = Number(data?.today?.summary?.cash_in || 0);
  const cashOut = Number(data?.today?.summary?.cash_out || 0);
  const tone = cih >= 0 ? "emerald" : "rose";

  return (
    <div
      className={
        "rounded-2xl border-2 p-4 shadow-sm " +
        (tone === "emerald"
          ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60 dark:border-emerald-900/60 dark:from-emerald-950/40 dark:via-card dark:to-emerald-950/20"
          : "border-rose-300 bg-gradient-to-br from-rose-50 via-white to-rose-50/60 dark:border-rose-900/60 dark:from-rose-950/40 dark:via-card dark:to-rose-950/20")
      }
      data-testid="manager-cashbook-widget"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Cashbook · Live
          </div>
          <div className="mt-1 font-display text-3xl font-bold tabular-nums" data-testid="widget-cih">
            Rs. {loading ? "…" : rupee(cih)}
          </div>
          <div className="text-[11px] text-muted-foreground">{t("components.managercashbookwidget.cash_in_hand_rolling")}</div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            asChild
            data-testid="widget-open-cashbook"
          >
            <Link to="/manager/cashbook">{t("components.managercashbookwidget.open_cashbook")}</Link>
          </Button>
          <Button
            size="sm"
            onClick={() => setShowDeposit(true)}
            data-testid="widget-quick-deposit"
          >
            <Landmark className="h-4 w-4" /> Quick deposit
          </Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-border bg-background p-2.5">
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            <TrendingUp className="h-3 w-3" /> Today IN
          </div>
          <div className="mt-0.5 font-mono text-base font-bold tabular-nums" data-testid="widget-cash-in">
            Rs. {rupee(cashIn)}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-2.5">
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
            <TrendingDown className="h-3 w-3" /> Today OUT
          </div>
          <div className="mt-0.5 font-mono text-base font-bold tabular-nums" data-testid="widget-cash-out">
            Rs. {rupee(cashOut)}
          </div>
        </div>
      </div>
      {showDeposit && (
        <QuickDepositDialog
          suggested={cih}
          onClose={() => setShowDeposit(false)}
          onSaved={() => { setShowDeposit(false); load(); }}
        />
      )}
    </div>
  );
}

function QuickDepositDialog({ suggested, onClose, onSaved }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [bank, setBank] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error(t("csa.csaportal.enter_a_valid_amount"));
    setSaving(true);
    try {
      await api.post("/manager/bank-deposits", {
        amount: amt,
        bank_name: bank.trim() || null,
        note: note.trim() || null,
      });
      toast.success(t("components.managercashbookwidget.bank_deposit_recorded"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      data-testid="quick-deposit-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <div className="text-lg font-bold">{t("components.managercashbookwidget.quick_deposit")}</div>
          <div className="text-xs text-muted-foreground">
            Suggested: Rs. {rupee(Math.max(0, suggested))}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Amount
            </label>
            <input
              autoFocus
              inputMode="decimal"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-lg font-mono tabular-nums"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              data-testid="qd-amount"
            />
            {suggested > 0 && (
              <button
                type="button"
                className="mt-1 text-[10px] font-semibold text-primary underline"
                onClick={() => setAmount(String(Math.round(suggested)))}
                data-testid="qd-fill-cih"
              >
                Deposit full Rs. {rupee(suggested)}
              </button>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Bank (optional)
            </label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              placeholder="HDFC, SBI, ICICI…"
              data-testid="qd-bank"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Note
            </label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reference / slip #"
              data-testid="qd-note"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="qd-submit">
            {saving ? "Saving…" : "Deposit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
